import 'swiper/css';
import 'swiper/css/pagination';

import Grid2 from '@mui/material/Unstable_Grid2';
import { clsx } from 'clsx';
import { useId, useRef, useState } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import Eyebrow from '@/components/Eyebrow/Eyebrow';
import Icon, { IconName } from '@/components/Icon/Icon';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer';
import { FrontifyImageContent } from '@/contentful';
import useViewPortSize from '@/hooks/useViewportSize';
import { brandColors } from '@/styles/sunrunTheme.styles';
import { PlaylistVideoCardCf } from '@/types';
import { getFrontifyBackgroundImageUrl } from '@/utils/frontify';

import useStyles from './Playlist.styles';
import { PlaylistVideoCard } from './PlaylistVideoCard';

export const Playlist = ({
  videos,
  isOnDark,
}: {
  isOnDark: boolean;
  videos: PlaylistVideoCardCf[];
}) => {
  const { classes } = useStyles({ isOnDark });
  const [isPlaying, setIsPlaying] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(true);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const activeVideo = videos[activeVideoIndex] || videos[0];
  const [thumbnail] = activeVideo.fields.thumbnail as unknown as [
    FrontifyImageContent
  ];
  const swiperRef = useRef<SwiperClass>(null);
  const prevElId = useId().replaceAll(':', '');
  const nextElId = useId().replaceAll(':', '');
  const [isMuted, setIsMuted] = useState(false);
  const { isMobile } = useViewPortSize();

  const handlePlaybackStart = (index: number) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (activeVideoIndex !== index) {
      if (isIOS) setIsMuted(true);
      setActiveVideoIndex(index);
      setIsPlaying(true);
      swiperRef.current?.slideTo(index);
    }

    if (activeVideoIndex === index) {
      setIsPlaying((prev) => !prev);
    }

    setShowThumbnail(false);
  };

  const handleVideoEnd = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (activeVideoIndex + 1 < videos.length) {
      if (isIOS) setIsMuted(true);
      setActiveVideoIndex(activeVideoIndex + 1);
      setIsPlaying(true);
      swiperRef.current?.slideTo(activeVideoIndex);
    }

    if (activeVideoIndex + 1 === videos.length) {
      setIsPlaying(false);
      setActiveVideoIndex(0);
      setIsMuted(false);
      setShowThumbnail(true);
      swiperRef.current?.slideTo(0);
    }
  };

  return (
    <div className={classes.playlistContainer}>
      <Grid2 xs={12} className={classes.grid}>
        <div className={classes.videoContainer}>
          <div
            className={classes.playerOverlay}
            role='button'
            tabIndex={0}
            aria-label={`Play ${activeVideo.fields.headline} video`}
            style={{
              backgroundImage: getFrontifyBackgroundImageUrl(
                thumbnail.preview_url,
                isMobile ? 768 : 1440
              ),
              zIndex: showThumbnail ? 1 : -1,
            }}
            onClick={() => handlePlaybackStart(0)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handlePlaybackStart(0);
            }}
          >
            <Icon name={IconName.PLAY_SQUARE} width={80} />
          </div>
          <VideoPlayer
            key={activeVideoIndex}
            id='playlist-vimeo-player'
            data-testid='playlist-video-player'
            className={classes.videoPlayer}
            url={activeVideo.fields.vimeoUrl}
            playing={isPlaying}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={handleVideoEnd}
            controls
            muted={isMuted}
            playsinline
            width={'100%'}
            height={'100%'}
            config={{
              playerOptions: { color: brandColors.chargeBlue.substring(1) },
            }}
          />
        </div>

        <div className={classes.eyebrowWrapper}>
          <Eyebrow text='PLAYLIST' isOnDark={isOnDark} />
        </div>

        <Swiper
          navigation={{
            prevEl: `#${prevElId}`,
            nextEl: `#${nextElId}`,
            disabledClass: classes.navigationButtonDisabled,
          }}
          modules={[Navigation]}
          slidesPerView={'auto'}
          onSwiper={(swiper: SwiperClass) => {
            swiperRef.current = swiper;
          }}
        >
          {videos.map((card, index) => (
            <SwiperSlide className={classes.swiperSlide} key={index}>
              <PlaylistVideoCard
                isOnDark={isOnDark}
                key={card.sys.id}
                card={card}
                position={index + 1}
                length={videos.length}
                isPlaying={isPlaying && index === activeVideoIndex}
                onThumbnailClick={() => handlePlaybackStart(index)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Grid2>

      <div
        className={clsx(classes.navigation, {
          [classes.navigationHidden]:
            !swiperRef.current?.allowSlideNext &&
            !swiperRef.current?.allowSlidePrev,
        })}
      >
        <div id={prevElId} className={classes.navigationButton}>
          <Icon name={IconName.ARROW_LEFT} width={24} />
        </div>
        <div id={nextElId} className={classes.navigationButton}>
          <Icon name={IconName.ARROW_RIGHT} width={24} />
        </div>
      </div>
    </div>
  );
};
